import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

const GiftModal = ({ isOpen, onClose, creatorId, creatorName }) => {
  const [gifts, setGifts] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingGifts, setLoadingGifts] = useState(true);
  
  useEffect(() => {
    if (isOpen) {
      fetchGifts();
    }
  }, [isOpen]);
  
  const fetchGifts = async () => {
    try {
      const response = await axios.get(`${API}/gifts/available`);
      setGifts(response.data.gifts || []);
      if (response.data.gifts?.length > 0) {
        setSelectedGift(response.data.gifts[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar presentes:', error);
      toast.error('Erro ao carregar presentes');
    } finally {
      setLoadingGifts(false);
    }
  };
  
  const handleSendGift = async () => {
    if (!selectedGift) {
      toast.error('Selecione um presente');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userId = userRes.data.id;
      
      const response = await axios.post(
        `${API}/gifts/checkout?user_id=${userId}`,
        {
          creator_id: creatorId,
          gift_id: selectedGift.id,
          message: message || null,
          quantity: quantity
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirecionar para checkout do Mercado Pago
      if (response.data.url) {
        window.location.href = response.data.url;
      }
      
    } catch (error) {
      console.error('Erro ao enviar presente:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar presente');
    } finally {
      setLoading(false);
    }
  };
  
  const totalPrice = selectedGift ? selectedGift.price * quantity : 0;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#1A1A1A] border-[#C79F5E]/30 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#C79F5E] text-xl flex items-center gap-2">
            🎁 Enviar Presente
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Envie um presente para {creatorName}
          </DialogDescription>
        </DialogHeader>
        
        {loadingGifts ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#C79F5E] border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Seleção de Presente */}
            <div className="grid grid-cols-2 gap-3">
              {gifts.map((gift) => (
                <Card
                  key={gift.id}
                  className={`cursor-pointer transition-all hover:scale-105 ${
                    selectedGift?.id === gift.id
                      ? 'bg-[#4D1A4A]/30 border-[#C79F5E]'
                      : 'bg-[#0D0D0D] border-[#2A2A2A] hover:border-[#C79F5E]/50'
                  }`}
                  onClick={() => setSelectedGift(gift)}
                >
                  <CardContent className="p-3 text-center">
                    <div className="text-3xl mb-1">{gift.icon}</div>
                    <p className="font-semibold text-white text-sm">{gift.name}</p>
                    <p className="text-[#C79F5E] font-bold">R$ {gift.price.toFixed(2)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Quantidade */}
            <div className="flex items-center justify-between bg-[#0D0D0D] rounded-lg p-3">
              <span className="text-gray-400">Quantidade:</span>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="border-[#C79F5E]/30 h-8 w-8 p-0"
                >
                  -
                </Button>
                <span className="text-white font-semibold w-8 text-center">{quantity}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuantity(Math.min(10, quantity + 1))}
                  className="border-[#C79F5E]/30 h-8 w-8 p-0"
                >
                  +
                </Button>
              </div>
            </div>
            
            {/* Mensagem */}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Mensagem (opcional)</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Escreva uma mensagem..."
                className="bg-[#0D0D0D] border-[#2A2A2A] text-white resize-none"
                rows={2}
                maxLength={200}
              />
            </div>
            
            {/* Total */}
            <div className="bg-gradient-to-r from-[#4D1A4A]/30 to-[#C79F5E]/10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-400 text-sm">Total a pagar:</p>
                  <p className="text-2xl font-bold text-[#C79F5E]">
                    R$ {totalPrice.toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-xs">Criador recebe 80%</p>
                  <p className="text-green-400 font-semibold">
                    R$ {(totalPrice * 0.8).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Botões */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 border-[#C79F5E]/30"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSendGift}
                disabled={loading || !selectedGift}
                className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <>
                    {selectedGift?.icon} Enviar Presente
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GiftModal;
